import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Link } from 'react-router-dom';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import Layout from '../component/layout';

import WaterMeterLogs from './Water_meter_logs';
import PowerMeterLogs from './Power_meter_logs';
import MpesaLogs from './Mpesa_logs';
import BackendLogs from './Backend_logs';

const AuditLogsIndex = () => {
    const [stats, setStats] = useState({
        totalLogs: 0,
        authorizedRequests: 0,
        unauthorizedRequests: 0,
        uniqueIPs: 0,
        uniqueUsers: 0,
        methodBreakdown: {
            GET: 0,
            POST: 0,
            PUT: 0,
            DELETE: 0,
            PATCH: 0
        }
    });
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2('/api/core/audit/stats', 'GET');
            console.log("stats_response", response);

            if (response.success) {
                setStats(response.data.stats);
            } else {
                toastify('Failed to fetch audit stats', 'error');
            }
        } catch (error) {
            toastify('Error fetching audit stats: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        setRefreshing(true);
        await fetchStats();
        setRefreshing(false);
        toastify('Data refreshed successfully!', 'success');
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const StatCard = ({ title, value, icon, color = "primary" }) => (
        <div className="col-xl-3 col-md-6 mb-3">
            <div className={`card bg-${color} text-white border-0 shadow-sm h-100`}>
                <div className="card-body">
                    <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                            <h6 className="text-white-50 mb-2">{title}</h6>
                            <h3 className="mb-0 text-white">
                                {loading ? (
                                    <Skeleton width="60px" height="32px" />
                                ) : (
                                    typeof value === 'number' ? value.toLocaleString() : value
                                )}
                            </h3>
                        </div>
                        <div className="flex-shrink-0">
                            <i className={`${icon} fs-2 text-white-50`}></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const MethodBadge = ({ method, count }) => (
        <Badge
            value={`${method}: ${count}`}
            severity={
                method === 'GET' ? 'info' :
                    method === 'POST' ? 'success' :
                        method === 'PUT' ? 'warning' :
                            method === 'DELETE' ? 'danger' : 'secondary'
            }
            className="me-2 mb-2"
        />
    );

    return (
        <Layout>
            {/* Header */}
            <div className="page-header bg-light border-bottom">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <ul className="breadcrumb mb-2">
                                <li className="breadcrumb-item">
                                    <Link to="/core/dashboard" className="text-muted">
                                        <i className="ti ti-home me-1"></i>Dashboard
                                    </Link>
                                </li>
                                <li className="breadcrumb-item active text-dark">
                                    Audit Logs
                                </li>
                            </ul>
                            <div className="page-header-title">
                                <h2 className="mb-1 text-dark fw-semibold">
                                    <i className="ti ti-activity me-2 text-primary"></i>Audit Logs Management
                                </h2>
                                <p className="mb-0 text-muted">
                                    Monitor system activities and track user actions
                                </p>
                            </div>
                        </div>
                        <div className="col-md-4 text-end">
                            <Button
                                icon="ti ti-refresh"
                                className="p-button-outlined p-button-secondary"
                                onClick={refreshData}
                                loading={refreshing}
                                tooltip="Refresh Data"
                                label="Refresh"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    {/* Statistics Cards */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header">
                            <h5 className="mb-0">
                                <i className="ti ti-chart-bar me-2"></i>Audit Statistics Overview
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <StatCard
                                    title="Total Logs"
                                    value={stats.totalLogs}
                                    icon="ti ti-activity"
                                    color="primary"
                                />
                                <StatCard
                                    title="Authorized Requests"
                                    value={stats.authorizedRequests}
                                    icon="ti ti-shield-check"
                                    color="success"
                                />
                                <StatCard
                                    title="Unauthorized Requests"
                                    value={stats.unauthorizedRequests}
                                    icon="ti ti-shield-x"
                                    color="danger"
                                />
                                <StatCard
                                    title="Unique Users"
                                    value={stats.uniqueUsers}
                                    icon="ti ti-users"
                                    color="info"
                                />
                            </div>

                            <div className="row mt-4">
                                <div className="col-md-6">
                                    <div className="bg-light p-3 rounded">
                                        <h6 className="fw-bold mb-2">
                                            <i className="ti ti-world me-2"></i>Unique IP Addresses:
                                            <Badge value={stats.uniqueIPs} severity="secondary" className="ms-2" />
                                        </h6>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="bg-light p-3 rounded">
                                        <h6 className="fw-bold mb-2">
                                            <i className="ti ti-http-method me-2"></i>HTTP Methods Breakdown:
                                        </h6>
                                        <div className="mt-2">
                                            {Object.entries(stats.methodBreakdown).map(([method, count]) => (
                                                <MethodBadge key={method} method={method} count={count} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs for different log types */}
                    <div className="card border-0 shadow-sm">
                        <div className="card-header">
                            <h5 className="mb-0">
                                <i className="ti ti-list-details me-2"></i>System Logs
                            </h5>
                        </div>
                        <div className="card-body">
                            <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                                <TabPanel
                                    header="Backend Logs"
                                    leftIcon="ti ti-server me-2"
                                    headerTemplate={(options) => (
                                        <div className="flex align-items-center gap-2 p-3" onClick={options.onClick}>
                                            <i className="ti ti-server"></i>
                                            <span className="font-bold white-space-nowrap">Backend Logs</span>
                                            <Badge value={stats.totalLogs} className="ml-auto" />
                                        </div>
                                    )}
                                >
                                    <BackendLogs />
                                </TabPanel>

                                <TabPanel
                                    header="Water Meter Logs"
                                    leftIcon="ti ti-droplet me-2"
                                    headerTemplate={(options) => (
                                        <div className="flex align-items-center gap-2 p-3" onClick={options.onClick}>
                                            <i className="ti ti-droplet"></i>
                                            <span className="font-bold white-space-nowrap">Water Meter</span>
                                        </div>
                                    )}
                                >
                                    <WaterMeterLogs />
                                </TabPanel>

                                <TabPanel
                                    header="Power Meter Logs"
                                    leftIcon="ti ti-bolt me-2"
                                    headerTemplate={(options) => (
                                        <div className="flex align-items-center gap-2 p-3" onClick={options.onClick}>
                                            <i className="ti ti-bolt"></i>
                                            <span className="font-bold white-space-nowrap">Power Meter</span>
                                        </div>
                                    )}
                                >
                                    <PowerMeterLogs />
                                </TabPanel>

                                <TabPanel
                                    header="M-Pesa Logs"
                                    leftIcon="ti ti-credit-card me-2"
                                    headerTemplate={(options) => (
                                        <div className="flex align-items-center gap-2 p-3" onClick={options.onClick}>
                                            <i className="ti ti-credit-card"></i>
                                            <span className="font-bold white-space-nowrap">M-Pesa</span>
                                        </div>
                                    )}
                                >
                                    <MpesaLogs />
                                </TabPanel>
                            </TabView>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AuditLogsIndex;